import { applyPatch } from 'diff'
import CryptoJS from 'crypto-js'
export { toCamelCase, toPascalCase, descriptionToPath, groupAndMergeReadModels } from '@/shared/codegen/utils'

export const renderTree = (fileObject, indent = '', isRoot = true) => {
  let output = ''
  const keys = Object.keys(fileObject)

  if (isRoot) {
    const rootName = keys[0]
    output += `${rootName}/\n`
    return output + renderTree(fileObject[rootName], '', false)
  }

  keys.forEach((key, index) => {
    const isLast = index === keys.length - 1
    const prefix = isLast ? '└── ' : '├── '
    const nestedIndent = isLast ? '    ' : '│   '

    if (Object.keys(fileObject[key]).length > 0) { // It's a directory
      output += `${indent}${prefix}${key}/\n`
      output += renderTree(fileObject[key], indent + nestedIndent, false)
    } else { // It's a file
      output += `${indent}${prefix}${key}\n`
    }
  })

  return output
}

export const flattenTree = (tree, opened, basePath = '', depth = 0) => {
  const result = []

  Object.entries(tree)
    .sort(([nameA, childrenA], [nameB, childrenB]) => {
      const isFolderA = childrenA && typeof childrenA === 'object' && Object.keys(childrenA).length > 0
      const isFolderB = childrenB && typeof childrenB === 'object' && Object.keys(childrenB).length > 0

      if (isFolderA !== isFolderB) {
        return isFolderA ? -1 : 1 // folders first
      }

      return nameA.localeCompare(nameB) // alphabetical by name
    })
    .forEach(([name, children]) => {
      const fullPath = basePath ? `${basePath}/${name}` : name
      const isFolder = children && typeof children === 'object' && Object.keys(children).length > 0
      const isOpened = isFolder && opened.includes(fullPath)

      result.push({
        name,
        path: fullPath,
        fullPath,
        isFolder,
        isOpened,
        depth
      })

      if (isFolder && isOpened) {
        result.push(...flattenTree(children, opened, fullPath, depth + 1))
      }
    })

  return result
}

const isObject = (value) => value && typeof value === 'object' && !Array.isArray(value)

export const collapsePaths = (node) => {
  // If the node is not an object or is empty (like a file), return it as is.
  if (!isObject(node) || Object.keys(node).length === 0) {
    return node
  }

  return Object.keys(node).reduce((acc, key) => {
    let currentPath = key
    let currentNode = node[key]

    // Keep traversing as long as the current node is an object with exactly one key.
    // This identifies a chain of single nested folders.
    const nodeKeys = Object.keys(currentNode)
    while (isObject(currentNode) && nodeKeys.length === 1) {
      const singleKey = nodeKeys[0]

      // Stop if the single child is a file (an empty object).
      if (Object.keys(currentNode[singleKey]).length === 0) {
        break
      }

      // Append the nested folder to the path and move down the tree.
      currentPath += `/${singleKey}`
      currentNode = currentNode[singleKey]

      // Update keys for the next iteration check.
      Object.assign(nodeKeys, Object.keys(currentNode))
    }

    // Assign the processed (and recursively collapsed) node to the new collapsed path.
    acc[currentPath] = collapsePaths(currentNode)
    return acc
  }, {})
}

export const applyGitDiff = (originalContent, diffString) => {
  // The diff library's applyPatch function expects the diff to not have the --- a/file and +++ b/file headers.
  // We also need to ensure consistent line endings for the original content and the patch.
  const cleanDiffString = diffString
    .split('\n')
    .slice(2) // Remove the first two lines (--- and +++)
    .map(line => line.replace(/\u00A0/g, ' '))
    .join('\n')

  // Ensure consistent line endings for original content
  const normalizedOriginalContent = originalContent.replace(/\r\n/g, '\n')

  const patchedContent = applyPatch(normalizedOriginalContent, cleanDiffString)

  if (patchedContent === false) {
    console.error('Failed to apply patch. The diff may not be applicable to the original content.')
    return false
  }
  return patchedContent
}

export const getLanguageFromFilename = (filename) => {
  const mapExtensionToLanguage = {
    js: 'javascript',
    cs: 'csharp',
    json: 'json',
    md: 'markdown',
    yaml: 'yaml',
    env: 'shell',
    '': 'plaintext'
  }

  const extension = filename.split('.').pop()
  // TODO: this function seems to run every time chatGPT gives us a response. It should be optimized.
  // console.log(extension, mapExtensionToLanguage[extension])
  return mapExtensionToLanguage[extension] || 'plaintext'
}

export const identifySchemaType = (example) => {
  if (example === null || example === undefined) {
    return 'string'
  }
  if (!isNaN(example) && !isNaN(parseFloat(example))) {
    return Number.isInteger(parseFloat(example)) ? 'integer' : 'number'
  }
  if (typeof example === 'boolean' || example === 'true' || example === 'false') {
    return 'boolean'
  }
  return 'string'
}

export const calcGitBlobHash = (content) => {
  const normalizedContent = content.replace(/\r\n/g, '\n')
  const byteLength = new TextEncoder().encode(normalizedContent).length
  const header = `blob ${byteLength}\0`
  const store = header + normalizedContent

  return CryptoJS.SHA1(store).toString(CryptoJS.enc.Hex)
}

const getItemKey = (item) => {
  // For merged read models with multiple IDs, use sorted allIds as composite key.
  if (item.allIds && Array.isArray(item.allIds) && item.allIds.length > 1) {
    return item.allIds.slice().sort().join(',')
  }
  return item.id
}

export const diffArrays = (A, B) => {
  const mapA = new Map(A.map(item => [getItemKey(item), item]))
  const mapB = new Map(B.map(item => [getItemKey(item), item]))

  const results = []

  // Removed or updated
  for (const [key, itemA] of mapA.entries()) {
    if (!mapB.has(key)) {
      results.push({ type: 'delete', old: itemA })
    } else {
      const itemB = mapB.get(key)
      // if (!_.isEqual(itemA, itemB)) {
      if (JSON.stringify(itemA) !== JSON.stringify(itemB)) {
        results.push({ type: 'update', old: itemA, new: itemB })
      }
    }
  }

  // Added
  for (const [key, itemB] of mapB.entries()) {
    if (!mapA.has(key)) {
      results.push({ type: 'create', new: itemB })
    }
  }

  return results
}

export const toVerb = (s) => s[0].toUpperCase() + s.slice(1, -1) + 'ing'
